import React, { useCallback, useMemo, useState } from 'react';
import { Box, ButtonBase, makeStyles } from '@material-ui/core';
import _ from 'lodash';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import { BsLinkIcon, DropDownIcon, DropUpIcon } from '../../../../components/Icon';
import { BedStatusIcon } from '../../components';
import LinkAllRelatedBed from '../Popups/LinkAllRelatedBed';
import { bedStatusAPI } from '../../../../apis';
import { AutoCompleteSelect } from '../../../../components';
import StatusIcon from '../Icons/StatusBed';
import bs from '../../bsApp';

const useStyles = makeStyles(() => ({
  title: {
    fontFamily: 'NotoSans-Bold',
    fontSize: '1.25rem',
    color: '#2e4469',
  },
  subTitle: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.75rem',
    color: '#2e4469',
  },
  dropBtn: {
    width: '1.5rem',
    height: '1.5rem',
  },
  dropIcon: {
    fontSize: '1rem',
    color: '#538fff',
  },
  bedStatusIconBox: {
    position: 'relative',
    width: '5rem',
    height: '4.688rem',
    paddingTop: '1.563rem',
  },
  linkBtn: {
    color: '#538fff',
  },
  bedRotation: {
    fontFamily: 'NotoSans-Medium',
    fontSize: '0.875rem',
    color: '#666666',
  },
  rotateBedBox: {
    transform: 'scale(0.7)',
    marginRight: '0.5rem',
  },
}));

const BedsWaitingPlace = ({
  bedList = [],
  hospCode,
  wardLocation,
  getLinkListAPI,
  dragedBedList,
  rotate,
  setRotate,
  cubicleList,
  handleWaitCreateLinkedBed,
}) => {
  const classes = useStyles();

  const rotateList = [
    {
      text: '0°',
      value: 'top',
    },
    {
      text: '90°',
      value: 'right',
    },
    {
      text: '180°',
      value: 'bottom',
    },
    {
      text: '270°',
      value: 'left',
    },
  ];

  const handleRotate = useCallback((value) => {
    setRotate(value);
  }, []);

  return (
    <Box
      position="absolute"
      height="calc(100vh - 4.375rem - 4.5rem)"
      width="27.5rem"
      bgcolor="#fff"
      top="4.5rem"
      right="0"
      boxShadow="-5px 5px 14px 0 rgba(0, 0, 0, 0.05)"
      borderLeft="solid 1px #e4e7eb"
      padding="2.5rem 1.25rem 0"
      overflow="auto"
    >
      <Box display="flex" flexDirection="column" gridGap="0.25rem" pb="1.25rem">
        <div className={classes.title}>Beds Waiting for Place</div>
        <div className={classes.subTitle}>Please drag to add the bed into the zone</div>
      </Box>
      <Box display="flex" flexDirection="column" gridGap="0.75rem" pb="1.875rem">
        <div className={classes.bedRotation}>Bed Rotation</div>
        <AutoCompleteSelect
          itemList={rotateList}
          value={rotate}
          onChange={handleRotate}
          StartAdornment={
            <div className={classes.rotateBedBox}>
              <StatusIcon typeName={bs.TBC} bedNo="00" ward="3A" bedHead={rotate} />
            </div>
          }
        />
      </Box>
      {bedList.map((item) => (
        <WardPlace
          key={item.ccWardCd}
          {...item}
          hospCode={hospCode}
          wardLocation={wardLocation}
          getLinkListAPI={getLinkListAPI}
          dragedBedList={dragedBedList}
          rotate={rotate}
          cubicleList={cubicleList}
          handleWaitCreateLinkedBed={handleWaitCreateLinkedBed}
        />
      ))}
    </Box>
  );
};

const WardPlace = ({
  ccWardCd,
  wardBeds,
  hospCode,
  wardLocation,
  getLinkListAPI,
  dragedBedList,
  rotate,
  cubicleList,
  handleWaitCreateLinkedBed,
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [linkPopup, setLinkPopup] = useState(false);
  const [links, setLinks] = useState([]);

  const handleOpen = useCallback(() => {
    setOpen((o) => !o);
  }, []);

  const getLinksAPI = useCallback(async () => {
    try {
      const cubicleBeds = _.flatMap(
        _.flatMap(cubicleList, (item) => item.data.beds).filter((item) => item.data),
        (item) => item.data
      );

      console.log('cubicleList', cubicleBeds);
      const { result } = await bedStatusAPI.sortLinked({
        hospCode,
        wardLocation,
        wardCd: ccWardCd,
        bedNos: _.map(cubicleBeds, (item) => ({
          linkeds: item.linkeds,
        })),
      });

      setLinks(result.bedNos);
      setLinkPopup(true);
    } catch (error) {}
  }, [dragedBedList, ccWardCd, hospCode, wardLocation, cubicleList]);

  const handleLinkPopup = useCallback(
    (open) => () => {
      setLinkPopup(open);
      if (!open) {
        setLinks([]);
      }
    },
    []
  );

  const showBeds = useMemo(() => {
    return _.filter(
      wardBeds,
      (item) => !_.some(dragedBedList, (dragedItem) => dragedItem.bedStatusId === item.bedStatusId)
    );
  }, [wardBeds, dragedBedList]);

  return (
    <>
      <Box mt="1.25rem">
        <Box display="flex" justifyContent="space-between">
          <Box display="flex" gridGap="0.625rem">
            <div className={classes.title}>{ccWardCd}</div>
            {showBeds.length > 0 && (
              <ButtonBase className={classes.linkBtn} onClick={getLinksAPI}>
                <BsLinkIcon />
              </ButtonBase>
            )}
          </Box>
          <ButtonBase className={classes.dropBtn} onClick={handleOpen}>
            {open ? <DropDownIcon className={classes.dropIcon} /> : <DropUpIcon className={classes.dropIcon} />}
          </ButtonBase>
        </Box>
        {open && (
          <Droppable
            droppableId={JSON.stringify({
              type: 'Wait',
              ccWardCd,
            })}
            isDropDisabled={true}
          >
            {(provided) => (
              <Box
                display="grid"
                gridTemplateColumns="1fr 1fr 1fr 1fr"
                gridRowGap="1.25rem"
                gridColumnGap="0.5rem"
                ref={provided.innerRef}
                {...provided.droppableProps}
              >
                {_.map(showBeds, (item, index) => {
                  return (
                    <Box key={item.bedStatusId} className={classes.bedStatusIconBox}>
                      <Draggable key={item.bedStatusId} draggableId={item.bedStatusId.toString()} index={index}>
                        {(dragProvider) => (
                          <Box
                            {...dragProvider.draggableProps}
                            {...dragProvider.dragHandleProps}
                            ref={dragProvider.innerRef}
                          >
                            <BedStatusIcon
                              className={classes.bedStatusIcon}
                              isAbsolute={true}
                              allData={{ ...item, bedHead: rotate }}
                              status={item.status}
                              bedNo={item.bedNo}
                              gender={item.patGender}
                              ward={ccWardCd}
                              disabled
                              editFloorPlan
                            />
                          </Box>
                        )}
                      </Draggable>
                    </Box>
                  );
                })}
                {provided.placeholder && <div style={{ display: 'none' }}>{provided.placeholder}</div>}
              </Box>
            )}
          </Droppable>
        )}
      </Box>
      {linkPopup && (
        <LinkAllRelatedBed
          links={links}
          handleClose={handleLinkPopup(false)}
          handleWaitCreateLinkedBed={handleWaitCreateLinkedBed}
        />
      )}
    </>
  );
};

export default BedsWaitingPlace;
